import {Injectable} from '@angular/core';
import {MonoTypeOperatorFunction, Observable, pipe} from 'rxjs';
import {filter, map, startWith} from 'rxjs/operators';
import type {WebSocketMessage} from '../../streaming/streaming-interfaces';
import {SubscriptionStreamingService} from '../../streaming/subscription-streaming.service';
import {SubscriptionSubjectType} from '../../streaming/subscription-subject-type';
import {PosFramesHistoryType} from '../dex-position-frames/pos-frames-history';
import {clearHeldPositionIdentifier, HeldPositionIdentifier} from './types/held-position';

type PosFramesSubjectStr = string;

type PosFramesPointsArray = number[];

enum DexStreamingPosFramesMsgType {
    POSITION_FRAME_SNAPSHOT = 'POSITION_FRAME_SNAPSHOT',
    POSITION_FRAME_UPDATE   = 'POSITION_FRAME_UPDATE'
}

export interface DexStreamingPosFramesSnapshotMsgData {
    requestId: string;
    data: PosFramesPointsArray;
}

export interface DexStreamingPosFramesUpdateMsgData {
    requestId: string;
    last: number;
}

interface DexStreamingPosFramesSnapshotMsg {
    messageType: DexStreamingPosFramesMsgType.POSITION_FRAME_SNAPSHOT;
    data: DexStreamingPosFramesSnapshotMsgData;
}

interface DexStreamingPosFramesUpdateMsg {
    messageType: DexStreamingPosFramesMsgType.POSITION_FRAME_UPDATE;
    data: DexStreamingPosFramesUpdateMsgData;
}

type SubjectMsg = DexStreamingPosFramesSnapshotMsg | DexStreamingPosFramesUpdateMsg;

interface DexPosFramesSubscriptionRec {
    posIdentifier: HeldPositionIdentifier;
    key: string;
    type: SubscriptionSubjectType<PosFramesPointsArray>;
    pointsArray: PosFramesPointsArray | undefined;
    lastUpdateTimestamp: string | undefined;
}

@Injectable()
export class DexPosFramesStreamingService {

    private readonly subsMap = new Map<PosFramesSubjectStr, DexPosFramesSubscriptionRec>();

    private requestIdCounter: number = 0;

    constructor(
        // eslint-disable-next-line @angular-eslint/prefer-inject
        private readonly subscriptionStreamingService: SubscriptionStreamingService
    ) {}

    public createSubscription(
        posIdentifier: HeldPositionIdentifier,
        type: PosFramesHistoryType
    ): Observable<PosFramesPointsArray> {
        const subRec = this.getOrSubscriptionRec(posIdentifier, type);

        return this.subscriptionStreamingService.createSubscription(subRec.type, [subRec.key]);
    }

    private getOrSubscriptionRec(
        posIdentifier: HeldPositionIdentifier,
        type: PosFramesHistoryType
    ): DexPosFramesSubscriptionRec {
        posIdentifier = clearHeldPositionIdentifier(posIdentifier);
        const key = [
            posIdentifier.accountId,
            posIdentifier.deliverableId,
            posIdentifier.positionId,
            type
        ].join(':');

        let subRec = this.subsMap.get(key);

        if (!subRec) {
            const requestId = String(++this.requestIdCounter);

            subRec = {
                posIdentifier:       clearHeldPositionIdentifier(posIdentifier),
                key,
                type:                undefined as any, // To be set below.
                pointsArray:         undefined,
                lastUpdateTimestamp: undefined
            } satisfies DexPosFramesSubscriptionRec;

            subRec.type = {
                name:               `DEX_POSITION_FRAME#${requestId}#${key}`,
                isMultiSubject:     true,
                requestId,
                operations:         {
                    SUBSCRIBE:   'SUBSCRIBE_POSITION_FRAME',
                    UNSUBSCRIBE: 'UNSUBSCRIBE_POSITION_FRAME'
                },
                messagePipe:        pipe(
                    filter((message: WebSocketMessage) =>
                        message.messageType in DexStreamingPosFramesMsgType
                        && (message as SubjectMsg).data.requestId === requestId
                    ),
                    map((message: SubjectMsg): PosFramesPointsArray => this.readMessage(subRec, message))
                ),
                subjectPipeFactory: () => this.createSubjectPipe(subRec),
                deprecateSubject:   () => this.subsMap.delete(key)
            } satisfies SubscriptionSubjectType<PosFramesPointsArray>;

            this.subsMap.set(key, subRec);
        }

        return subRec;
    }

    private createSubjectPipe(subRec: DexPosFramesSubscriptionRec): MonoTypeOperatorFunction<PosFramesPointsArray> {
        return source =>  subRec.pointsArray
            ? source.pipe(
                startWith(subRec.pointsArray)
            )
            : source;
    }

    private readMessage(subRec: DexPosFramesSubscriptionRec, message: SubjectMsg): PosFramesPointsArray {
        let result: PosFramesPointsArray = subRec.pointsArray;
        let isUpdated: boolean = false;

        switch (message.messageType) {
            case DexStreamingPosFramesMsgType.POSITION_FRAME_SNAPSHOT: {
                result = message.data.data;
                subRec.pointsArray = result;
                isUpdated = true;
                break;
            }
            case DexStreamingPosFramesMsgType.POSITION_FRAME_UPDATE: {
                result = subRec.pointsArray
                    ? [...subRec.pointsArray!.slice(1), message.data.last]
                    : [message.data.last];
                subRec.pointsArray = result;
                isUpdated = true;
                break;
            }
        }

        if (isUpdated) {
            subRec.lastUpdateTimestamp = (new Date()).toISOString();
        }

        return result;
    }

}
